import { createAction, createGetter, createMutation, mutate, on, select, StoreFeature } from '@softline/core';
import { UserRegistration } from '../data/user-registration.model';
import { RegistrationResourceService } from '../services/registration-resource.service';
import { take } from 'rxjs/operators';

export interface State {
  confirming: boolean;
  confirmed: boolean;
  registering: boolean;
  registered: boolean;
}

export const mutations = {
  startConfirmation: createMutation<State, void>('startConfirmation'),
  setConfirmed: createMutation<State, boolean>('setConfirmed'),
  startRegistration: createMutation<State, void>('startRegistration'),
  setRegistered: createMutation<State, boolean>('setRegistered')
};

export const getters = {
  confirmed: createGetter<State, undefined, boolean>('confirmed'),
  confirming: createGetter<State, undefined, boolean>('confirming')
};

export const actions = {
  confirm: createAction<State, {username: string, hash: string}, {username: string}>('confirm'),
  register: createAction<State, {user: UserRegistration}, {user: UserRegistration}>('register')
};

export const feature: StoreFeature<State> = {
  initialState: {
    registering: false,
    registered: false,
    confirming: false,
    confirmed: false,
  },
  mutations: [
    mutate(mutations.startConfirmation, ({ state }) => ({ ...state, confirming: true, confirmed: false })),
    mutate(mutations.setConfirmed, ({ state, params }) => ({ ...state, confirmed: params, confirming: false })),
    mutate(mutations.startRegistration, ({ state }) => ({ ...state, registering: true, registered: false })),
    mutate(mutations.setRegistered, ({ state, params }) => ({ ...state, registered: params, registering: false }))
  ],
  getters: [
    select(getters.confirmed, ({ state }) => state.confirmed),
    select(getters.confirming, ({ state }) => state.confirming)
  ],
  actions: [
    on(actions.confirm, async ({ state, store, featureName, injector, params }) => {
      store.commit(featureName, mutations.startConfirmation);
      const service = injector.get(RegistrationResourceService);
      const result = await service.confirm(params.username, params.hash).pipe(take(1)).toPromise();

      if (!result) {
        store.commit(featureName, mutations.setConfirmed, false);
        return null;
      } else {
        store.commit(featureName, mutations.setConfirmed, true);
        return { username: params.username };
      }
    }),
    on(actions.register, async ({ state, store, featureName, injector, params }) => {
      store.commit(featureName, mutations.startRegistration);
      const service = injector.get(RegistrationResourceService);
      const result = await service.register(params.user).pipe(take(1)).toPromise();

      if (!result || !result?.email) {
        store.commit(featureName, mutations.setRegistered, false);
        return null;
      } else {
        store.commit(featureName, mutations.setRegistered, true);
        return { user: result };
      }
    })
  ]
};
